import { hasValue, Vehicle } from "@alba-cars/common-modules";

/**
 * Truncates text to a maximum length
 */
export const truncateText = (text: string, maxLength: number = 100): string => {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + "...";
};

/**
 * Returns the first option from an array or the value if it's not an array
 */
export const getValueOrFirstOption = <T>(value: T | T[]): T => {
  if (Array.isArray(value)) {
    return value[0];
  }
  return value;
};

/**
 * Checks if an object has any defined values (not null or undefined or empty)
 */
export const hasDefinedValues = (obj?: Record<string, unknown>): boolean => {
  const cleanedObject = obj ? Object.fromEntries(Object.entries(obj).filter(([_, value]) => hasValue(value))) : {};
  return (
    hasValue(cleanedObject) &&
    Object.keys(cleanedObject).length > 0 &&
    Object.values(cleanedObject).some((value) => hasValue(value))
  );
};

/**
 * Returns the S3 URL for a given file name
 */
export const getS3Url = (fileName: string): string => {
  return `${process.env.NEXT_PUBLIC_AWS_S3_URL}/${fileName}`;
};

/**
 * Returns the title of a car, fallback to `make` and `model` if `title` is not available
 */
export const getCarNameTitle = (
  vehicle: Vehicle,
  { showTrim = false, showYear = false }: { showTrim?: boolean; showYear?: boolean },
): string => {
  if (vehicle.title && vehicle.useCustomTitle) {
    return vehicle.title;
  } else {
    let result = `${vehicle.make.name} ${vehicle.model.name}`;
    const notAllowedTrims = ["other", "others", "standard"];

    if (showTrim && vehicle.trim && !notAllowedTrims.includes(vehicle.trim.toLowerCase())) {
      result += ` ${vehicle.trim}`;
    }

    if (showYear && vehicle.year) {
      result += ` ${vehicle.year}`;
    }

    return result.trim();
  }
};

/**
 * Generates a unique UUID, fallback to `Math.random()` if `crypto` is not available
 */
export function generateUniqueId(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  } else {
    // Fallback method: using crypto.getRandomValues if available
    if (typeof crypto !== "undefined" && typeof crypto.getRandomValues === "function") {
      const array = new Uint8Array(16);
      crypto.getRandomValues(array);
      array[6] = (array[6] & 0x0f) | 0x40;
      array[8] = (array[8] & 0x3f) | 0x80;
      return [...array]
        .map((b, i) => {
          const hex = b.toString(16).padStart(2, "0");
          return [4, 6, 8, 10].includes(i) ? `-${hex}` : hex;
        })
        .join("");
    } else {
      // Fallback to Math.random() if crypto is not available
      return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      });
    }
  }
}

/**
 * Converts a number to a percentage
 * @example
 * numberAsPercentage(5) // 0.05 (5%)
 * numberAsPercentage(20) // 0.2 (20%)
 * numberAsPercentage(100) // 1 (100%)
 */
export const numberAsPercentage = (number: number) => {
  if (number <= 0) return 0;
  const formattedNumber = (number / 100).toFixed(2);

  return Number(formattedNumber);
};

interface S3ParsedUrl {
  bucketName: string;
  region: string;
  fileKey: string;
}

export function parseS3Url(url: string): S3ParsedUrl {
  const emptyResult: S3ParsedUrl = {
    bucketName: "",
    region: "",
    fileKey: "",
  };

  try {
    const urlObject = new URL(url);

    if (!urlObject.hostname.includes("amazonaws.com")) {
      return emptyResult;
    }

    //* Pattern 1: https://bucket-name.s3.region.amazonaws.com/file-key
    const virtualHostedMatch = urlObject.hostname.match(/^(.+)\.s3\.([^.]+)\.amazonaws\.com$/);
    if (virtualHostedMatch) {
      return {
        bucketName: virtualHostedMatch[1],
        region: virtualHostedMatch[2],
        fileKey: urlObject.pathname.substring(1),
      };
    }

    //* Pattern 2: https://s3.region.amazonaws.com/bucket-name/file-key
    const pathStyleMatch = urlObject.hostname.match(/^s3\.([^.]+)\.amazonaws\.com$/);

    if (pathStyleMatch) {
      const pathParts = urlObject.pathname.substring(1).split("/");
      if (pathParts.length >= 2) {
        return {
          bucketName: pathParts[0],
          region: pathStyleMatch[1],
          fileKey: pathParts.slice(1).join("/"),
        };
      }
    }

    //* Pattern 3: https://s3.amazonaws.com/bucket-name/file-key (legacy format, us-east-1)
    if (urlObject.hostname === "s3.amazonaws.com") {
      const pathParts = urlObject.pathname.substring(1).split("/");
      if (pathParts.length >= 2) {
        return {
          bucketName: pathParts[0],
          region: "us-east-1", // Default region for legacy format
          fileKey: pathParts.slice(1).join("/"),
        };
      }
    }

    return emptyResult;
  } catch (error) {
    return emptyResult;
  }
}

interface ImageProcessParams {
  width?: number | "auto";
  height?: number;
  quality?: number;
  format?: string;
  crop?: "fill" | "limit" | "fit";
}

const CLOUDINARY_CLOUD_NAME = "iqjdf3p9";
export function getOptimizedImageUrl(s3ImageUrl: string, params?: ImageProcessParams): string {
  const isS3Url = s3ImageUrl.includes("amazonaws.com");

  if (!isS3Url) {
    return s3ImageUrl;
  }

  const { width = 500, quality = 75, format = "auto", height, crop = "fill" } = params || {};

  const { bucketName, fileKey } = parseS3Url(s3ImageUrl);
  const isStagingBucket = bucketName === "alba-cars-pre";

  const usedWidth = width ? width.toString() : "";
  const usedQuality = quality ? quality.toString() : "";
  const usedFormat = format ? format.toString() : "auto";
  const usedHeight = height ? height.toString() : "";

  if (isStagingBucket) {
    if (usedWidth === "auto") {
      return `https://res.cloudinary.com/${CLOUDINARY_CLOUD_NAME}/image/upload/c_${crop},q_${usedQuality},f_${usedFormat}/s3-stage/${fileKey}`;
    }

    return `https://res.cloudinary.com/${CLOUDINARY_CLOUD_NAME}/image/upload/w_${usedWidth},c_${crop},q_${usedQuality},f_${usedFormat}/s3-stage/${fileKey}`;
  }

  let finalUrl = `${process.env.NEXT_PUBLIC_S3_URL}/${fileKey}?format=${usedFormat}&quality=${usedQuality || 75}`;

  if (usedWidth) {
    finalUrl += usedWidth === "auto" ? "" : `&width=${usedWidth}`;
  }

  if (usedHeight) {
    finalUrl += `&height=${usedHeight}`;
  }

  return finalUrl;
}
